import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import UnfoldMoreOutlinedIcon from "@mui/icons-material/UnfoldMoreOutlined";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Drawer from "@mui/material/Drawer";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import Popover from "@mui/material/Popover";
import type { Theme } from "@mui/material/styles";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";

import type { NavIcon, NavMode, NavPage } from "../../navigation";
import {
  SHELL_BACKDROP,
  SHELL_BAR,
  SHELL_SURFACE,
  shellAccent,
  shellColors,
  type ShellAppearance,
} from "./tokens";

/** An extra footer action in the phone switcher, e.g. Switch facility. */
export interface PhoneSheetAction {
  id: string;
  label: string;
  icon: NavIcon;
  /** Runs once the switcher has closed, so focus can move to what it opens. */
  onSelect: () => void;
}

export interface PhoneModeSwitcherProps<A> {
  modes: readonly NavMode<A>[];
  activeModeId: string | null;
  onSelectMode: (mode: NavMode<A>) => void;
  /** The user's own pages (e.g. My coaching), listed under the modes. */
  userPages?: readonly NavPage<A>[];
  /** The mode to name on the trigger when the URL is outside every mode. */
  fallbackModeId?: string | null;
  /** Extra footer actions, shown after the user's pages. */
  actions?: readonly PhoneSheetAction[];
  /** A bottom sheet (business) or a card under the bar (platform). */
  variant?: "sheet" | "dropdown";
  appearance?: ShellAppearance;
}

function menuPageLabels<A>(mode: NavMode<A>): string[] {
  const labels = mode.pages.filter((page) => !page.inUserMenu).map((page) => page.label);
  // A mode whose only page shares its name (Home) needs no page line.
  return labels.length === 1 && labels[0] === mode.label ? [] : labels;
}

function FooterIcon({ icon: Icon }: { icon: NavIcon }) {
  return (
    <Box
      component="span"
      aria-hidden="true"
      sx={(theme) => ({ display: "inline-flex", fontSize: 18, ...shellColors(theme, { color: "muted" }) })}
    >
      <Icon fontSize="inherit" />
    </Box>
  );
}

/**
 * Phone: the mode pill in the top bar. Business opens a bottom sheet; platform
 * opens a card under the bar. Both list every visible mode with its pages.
 */
export function PhoneModeSwitcher<A>({
  modes,
  activeModeId,
  onSelectMode,
  userPages = [],
  actions = [],
  fallbackModeId = null,
  variant = "sheet",
  appearance = "business",
}: PhoneModeSwitcherProps<A>) {
  const [open, setOpen] = useState(false);
  const [anchorTop, setAnchorTop] = useState(0);
  const pendingAction = useRef<(() => void) | null>(null);
  const active = modes.find((mode) => mode.id === activeModeId) ?? null;
  // Off-menu pages (no access, unclaimed URLs) name the person's start mode.
  const shown = active ?? modes.find((mode) => mode.id === fallbackModeId) ?? null;
  const label = shown?.label ?? "Menu";
  const ActiveIcon = shown?.icon;
  const dropdown = variant === "dropdown";

  const close = (action?: () => void) => {
    pendingAction.current = action ?? null;
    setOpen(false);
  };
  const runPendingAction = () => {
    const action = pendingAction.current;
    pendingAction.current = null;
    action?.();
  };

  const modeItems = modes.map((mode) => {
    const selected = mode.id === activeModeId;
    const pages = menuPageLabels(mode);
    const ModeIcon = mode.icon;
    return dropdown ? (
      <MenuItem
        key={mode.id}
        aria-current={selected ? "true" : undefined}
        onClick={() => {
          close();
          onSelectMode(mode);
        }}
        sx={(theme) => ({
          flexBasis: "100%",
          gap: "12px",
          py: "10px",
          px: 2,
          whiteSpace: "normal",
          ...(selected ? shellColors(theme, { backgroundColor: "activeTint" }) : {}),
          "&:hover": shellColors(theme, { backgroundColor: selected ? "activeTint" : "fill" }),
        })}
      >
        <Box
          component="span"
          aria-hidden="true"
          sx={(theme) => ({
            display: "inline-flex",
            fontSize: 24,
            ...shellColors(theme, { color: selected ? "activeTitle" : "ink" }),
          })}
        >
          <ModeIcon fontSize="inherit" />
        </Box>
        <Box component="span" sx={{ flex: 1, minWidth: 0, lineHeight: 1.3 }}>
          <Box
            component="span"
            sx={(theme) => ({
              display: "block",
              fontSize: 15.5,
              fontWeight: 600,
              ...shellColors(theme, { color: "ink" }),
            })}
          >
            {mode.label}
          </Box>
          {pages.length > 0 ? (
            <Box
              component="span"
              sx={(theme) => ({
                display: "block",
                fontSize: 12.5,
                ...shellColors(theme, { color: "muted" }),
              })}
            >
              {pages.join(", ")}
            </Box>
          ) : null}
        </Box>
        {selected ? (
          <CheckOutlinedIcon
            aria-hidden="true"
            sx={(theme) => ({ fontSize: 18, ...shellColors(theme, { color: "activeTitle" }) })}
          />
        ) : null}
      </MenuItem>
    ) : (
      <MenuItem
        key={mode.id}
        aria-current={selected ? "true" : undefined}
        onClick={() => {
          close();
          onSelectMode(mode);
        }}
        sx={(theme) => ({
          display: "block",
          flexBasis: "100%",
          whiteSpace: "normal",
          py: "6px",
          px: 0.5,
          borderRadius: "6px",
          borderBottom: 1,
          ...shellColors(theme, { borderColor: "line2" }),
        })}
      >
        <Box
          component="span"
          sx={(theme) => ({
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontSize: 15,
            fontWeight: 700,
            lineHeight: 1.4,
            ...shellColors(theme, { color: selected ? "activeTitle" : "ink" }),
          })}
        >
          {mode.label}
          {selected ? (
            <CheckOutlinedIcon
              aria-hidden="true"
              sx={(theme) => ({ fontSize: 18, ...shellColors(theme, { color: "accent" }) })}
            />
          ) : null}
        </Box>
        {pages.length > 0 ? (
          <Box
            component="span"
            sx={(theme) => ({
              display: "block",
              fontSize: 12,
              lineHeight: 1.4,
              ...shellColors(theme, { color: "muted" }),
            })}
          >
            {pages.join(" · ")}
          </Box>
        ) : null}
      </MenuItem>
    );
  });

  // One footer row: the user's pages on the left, extra actions on the right.
  const footerSx = (pushRight: boolean) => (theme: Theme) => ({
    flex: "0 0 auto",
    gap: "10px",
    px: dropdown ? 2 : 0.5,
    ml: pushRight ? "auto" : 0,
    borderRadius: "6px",
    fontSize: 13,
    ...shellColors(theme, { color: "ink2" }),
  });
  const footerItems = [
    ...userPages.map((page) => (
      <MenuItem
        key={page.id}
        component={Link}
        to={page.path}
        onClick={() => close()}
        sx={footerSx(false)}
      >
        <FooterIcon icon={page.icon} />
        {page.label}
      </MenuItem>
    )),
    ...actions.map((action, index) => (
      <MenuItem
        key={action.id}
        onClick={() => close(action.onSelect)}
        sx={footerSx(index === 0)}
      >
        <FooterIcon icon={action.icon} />
        {action.label}
      </MenuItem>
    )),
  ];

  const list = (
    <MenuList
      autoFocusItem
      aria-label="Modes"
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        py: 0,
      }}
    >
      {modeItems}
      {footerItems.length > 0 ? (
        <Box
          component="li"
          role="none"
          aria-hidden="true"
          sx={(theme) => ({
            flexBasis: "100%",
            height: dropdown ? 7 : 4,
            ...(dropdown
              ? { borderBottom: 1, mb: 0.75, ...shellColors(theme, { borderColor: "line" }) }
              : {}),
          })}
        />
      ) : null}
      {footerItems}
    </MenuList>
  );

  return (
    <>
      <ButtonBase
        type="button"
        aria-label={`Mode: ${label}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(event) => {
          const bar = event.currentTarget.closest("header");
          setAnchorTop((bar ?? event.currentTarget).getBoundingClientRect().bottom);
          setOpen(true);
        }}
        sx={{
          flexShrink: 0,
          borderRadius: "9px",
          "&:focus-visible": { outline: "none" },
          "&:focus-visible .shell-mode-pill": {
            outline: `2px solid ${SHELL_BAR.accent}`,
            outlineOffset: 1,
          },
        }}
      >
        <Box
          component="span"
          className="shell-mode-pill"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            height: dropdown ? 38 : 34,
            pl: dropdown ? 1.5 : 1.25,
            pr: 1.25,
            borderRadius: "9px",
            bgcolor: SHELL_BAR.raised,
            color: SHELL_BAR.strong,
            fontWeight: dropdown ? 600 : 700,
            fontSize: 15,
          }}
        >
          {ActiveIcon && !dropdown ? (
            <Box
              component="span"
              aria-hidden="true"
              sx={{ display: "inline-flex", fontSize: 18, color: SHELL_BAR.accentSoft }}
            >
              <ActiveIcon fontSize="inherit" />
            </Box>
          ) : null}
          <span>{label}</span>
          {dropdown ? (
            <UnfoldMoreOutlinedIcon
              aria-hidden="true"
              sx={{ fontSize: 20, color: SHELL_BAR.foreground }}
            />
          ) : (
            <ExpandMoreOutlinedIcon
              aria-hidden="true"
              sx={{ fontSize: 18, color: SHELL_BAR.muted }}
            />
          )}
        </Box>
      </ButtonBase>
      {dropdown ? (
        <Popover
          open={open}
          onClose={() => close()}
          anchorReference="anchorPosition"
          anchorPosition={{ top: anchorTop + 6, left: 10 }}
          slotProps={{
            backdrop: { invisible: false, sx: { bgcolor: SHELL_BACKDROP[appearance] } },
            transition: { onExited: runPendingAction },
            paper: {
              sx: (theme: Theme) => ({
                width: "calc(100% - 20px)",
                maxWidth: "none",
                py: "6px",
                borderRadius: "14px",
                boxShadow: SHELL_SURFACE.dropdownShadow,
                backgroundImage: "none",
                ...shellColors(theme, { backgroundColor: "surface" }),
              }),
            },
          }}
        >
          {list}
        </Popover>
      ) : (
        <Drawer
          anchor="bottom"
          open={open}
          onClose={() => close()}
          slotProps={{
            backdrop: { sx: { bgcolor: SHELL_BACKDROP[appearance] } },
            transition: { onExited: runPendingAction },
            paper: {
              sx: (theme: Theme) => ({
                borderRadius: "16px 16px 0 0",
                px: "10px",
                pt: 1,
                pb: "26px",
                maxHeight: "85dvh",
                boxShadow: SHELL_SURFACE.sheetShadow,
                backgroundImage: "none",
                ...shellColors(theme, { backgroundColor: "surface" }),
              }),
            },
          }}
        >
          <Box
            aria-hidden="true"
            sx={{
              width: 38,
              height: 4,
              borderRadius: 2,
              bgcolor: SHELL_SURFACE.sheetHandle,
              mx: "auto",
              mb: 1.25,
              flexShrink: 0,
            }}
          />
          {list}
        </Drawer>
      )}
    </>
  );
}

export interface PhonePageChipsProps<A> {
  mode: NavMode<A>;
  activePageId: string | null;
  badge?: (pageId: string) => ReactNode;
}

/** Phone: the active mode's pages as a horizontally scrolling chip row. */
export function PhonePageChips<A>({ mode, activePageId, badge }: PhonePageChipsProps<A>) {
  const pages = mode.pages.filter((page) => !page.inUserMenu);
  const activeChip = useRef<HTMLAnchorElement | null>(null);
  // Keep the current page's chip in view in the scrolling row.
  useEffect(() => {
    activeChip.current?.scrollIntoView?.({ block: "nearest", inline: "nearest" });
  }, [activePageId]);
  return (
    <Box
      component="nav"
      aria-label={`${mode.label} pages`}
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        height: 44,
        px: 1.5,
        borderBottom: 1,
        ...shellColors(theme, { backgroundColor: "surface", borderColor: "line" }),
        overflowX: "auto",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
      })}
    >
      {pages.map((page) => {
        const selected = page.id === activePageId;
        return (
          <ButtonBase
            key={page.id}
            component={Link}
            to={page.path}
            ref={selected ? activeChip : undefined}
            aria-current={selected ? "page" : undefined}
            sx={(theme) => ({
              flexShrink: 0,
              borderRadius: "14px",
              "&:focus-visible": { outline: "none" },
              "&:focus-visible .shell-page-chip": {
                outline: 2,
                ...shellAccent(theme, "outlineColor"),
                outlineOffset: 1,
              },
            })}
          >
            <Box
              component="span"
              className="shell-page-chip"
              sx={(theme) => ({
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                height: 28,
                px: 1.375,
                borderRadius: "14px",
                fontSize: 13,
                fontWeight: 600,
                whiteSpace: "nowrap",
                ...shellColors(
                  theme,
                  selected
                    ? { backgroundColor: "ink", color: "surface" }
                    : { backgroundColor: "fill", color: "ink3" },
                ),
              })}
            >
              {page.label}
              {badge?.(page.id)}
            </Box>
          </ButtonBase>
        );
      })}
    </Box>
  );
}
